import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { IndexedDbService } from 'src/app/services/indexed-db/indexed-db.service';
import { IsDeviceOnlineServiceV2 } from 'src/app/services/is-online/is-device-online.service';
import { IsDeviceOnlineService } from 'src/app/services/is-online/is-online.service';
import { LocalDatabaseVersionService } from 'src/app/services/local-database/local-database.service';

@Component({
  selector: 'app-connectivity-test',
  templateUrl: './connectivity-test.component.html',
  styleUrls: ['./connectivity-test.component.scss']
})
export class ConnectivityTestComponent implements OnInit, OnDestroy {
  isDeviceOnline = false;
  isDatabaseAlreadyUpdated = false;
  isDatabaseUpdateAvailable = false;
  isDatabaseUpdating = false;
  storedDatabaseVersion = 0.0;
  currentDatabaseVersion = 0.0;
  primaryColor = '#3490dc';

  readonly isLoading$ = new BehaviorSubject<boolean>(false);
  private readonly isDatabaseUpdateDownloadLoading$ = new BehaviorSubject<boolean>(false);

  get isDatabaseUpdateDownloadLoading() {
    return this.isDatabaseUpdateDownloadLoading$.getValue();
  }

  @ViewChild('btnUploadDownload', { static: true })
  btnUploadDownload: ElementRef<HTMLButtonElement>;

  subscriptions: Subscription[] = [];

  constructor(
    public readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly _isOnlineService: IsDeviceOnlineService,
    private readonly _isDeviceOnlineService: IsDeviceOnlineServiceV2,
    private readonly _http: HttpClient,
    private readonly _localDatabaseVersionService: LocalDatabaseVersionService,
    private readonly _indexedDbService: IndexedDbService
  ) {}

  ngOnInit(): void {
    const networkStatusSubscription = this._isOnlineService.networkStatus$
      .pipe(
        tap((networkStatus) => {
          switch (networkStatus) {
            case 'online': {
              this.isLoading$.next(true);
              this.isDeviceOnline = true;
              break;
            }
            case 'offline': {
              this.isLoading$.next(false);
              this.isDeviceOnline = false;
              this._resetStates();
              break;
            }
          }
        }),
        filter((networkStatus) => networkStatus === 'online'),
        switchMap(() => this._indexedDbService.checkLatestDatabaseVersion())
      )
      .subscribe({
        next: (response) => {
          // this.isDatabaseUpdateAvailable = true;
          if ('version' in response) {
            this.isLoading$.next(false);
            this.currentDatabaseVersion = response.version as number;
            this.storedDatabaseVersion =
              this._localDatabaseVersionService.getLocalDatabaseVersion();
            if (this.currentDatabaseVersion > this.storedDatabaseVersion) {
              this.isDatabaseUpdateAvailable = true;
            } else {
              this.isDatabaseAlreadyUpdated = true;
            }
          }
        }
      });

    this.subscriptions.push(networkStatusSubscription);

    const setupIndexedDbObjectStoresSubscription = this._indexedDbService
      .setupIndexedDbObjectStores()
      .pipe(
        filter((res) => res.status === 'success')
        // switchMap((res) => this._indexedDbService.checkLatestDatabaseVersion())
      )
      .subscribe({
        next: (res) => {
          console.log('_indexedDbService.setupIndexedDbObjectStores');
          console.log(res);
        }
      });

    this.subscriptions.push(setupIndexedDbObjectStoresSubscription);
  }

  logout() {
    this._authService.setStatus({ status: true }).subscribe();
    this._authService.Logout();
    this._router.navigate(['/']);
  }

  handleClickNext(): void {
    this._router.navigate([`/vin-information`]);
  }

  handleClickUploadDownload(): void {
    if (this.currentDatabaseVersion > this.storedDatabaseVersion) {
      this.isDatabaseUpdateDownloadLoading$.next(true);
      this._indexedDbService
        .downloadLatestDatabase()
        .pipe(switchMap((res) => this._indexedDbService.populateIndexedDb(res)))

        .subscribe({
          next: (response) => {
            if (response.status === 'success') {
              this.isDatabaseUpdateDownloadLoading$.next(false);
              this._indexedDbService.setPwaLocalDatabaseVersion(response.version);
              this.isDatabaseAlreadyUpdated = true;
              this.isDatabaseUpdateAvailable = false;
              this.storedDatabaseVersion = response.version;
            }
          }
        });
    } else {
      // it means that the database we have is the most up-to-date
      this.isDatabaseUpdateAvailable = false;
      this.isDatabaseAlreadyUpdated = true;
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length)
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  deleteIdbData() {
    this._indexedDbService
      .clearDataBase()
      .pipe(
        tap((res) => {
          this.storedDatabaseVersion = 0.0;
          this.isDatabaseAlreadyUpdated = false;
        }),
        switchMap(() => this._indexedDbService.checkLatestDatabaseVersion())
      )
      .subscribe({
        next: (res) => {
          if ('version' in res) {
            this.isLoading$.next(false);
            this.currentDatabaseVersion = res.version as number;
            // this.storedDatabaseVersion = this._localDatabaseVersionService.getLocalDatabaseVersion();
            this.storedDatabaseVersion = this._indexedDbService.getPwaLocalDatabaseVersion();
            if (this.currentDatabaseVersion > this.storedDatabaseVersion) {
              this.isDatabaseUpdateAvailable = true;
            } else {
              this.isDatabaseAlreadyUpdated = true;
            }
          }
        }
      });
  }

  private _resetStates(): void {
    // this.isDatabaseAlreadyUpdated = false;
    this.isDatabaseUpdateAvailable = false;
    this.isDatabaseUpdating = false;
  }
}
